import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { ReportTimeframeKey } from "../hooks/computePrevWindow";
import type { ReportTabKey, RequestCompare } from "./ReportsV2Context";

/**
 * Reports v2 state.
 *
 * Lives in Redux (not React Context) so it SURVIVES ROUTING — exactly the move
 * `dashboardV2Slice` made for the dashboard. `<ReportsV2Provider>` unmounts the
 * moment you navigate to /leads (or open a lead from a drill ↗), which used to
 * drop every filter, pivot, selected stage and sub-tab on the floor. The store
 * outlives the route, so coming back to /report lands you exactly where you
 * were. In-memory only (NOT redux-persist): a hard reload still starts clean,
 * which is the expected behaviour for report filters and matches the dashboard.
 *
 * Two halves:
 *  - the GLOBAL filter bar (timeframe/branch/role/user/intakes/compare + tab),
 *    which is *supposed* to re-scope every card at once, and
 *  - `widgets`, a flat bag of per-card UI state keyed by a scoped string
 *    ("leadOverview.segregation.pivot"). Flat + string-keyed is deliberate:
 *    `useWidgetState` subscribes to ONE key, so card A writing its key cannot
 *    re-render card B. That is the isolation half of the contract — a lifted
 *    `useState` in a report container re-renders every sibling card on every
 *    keystroke; a key in here re-renders only its own subscriber.
 *
 * Values MUST be JSON-serializable (redux's serializableCheck enforces it in
 * dev): ids, strings, numbers, booleans, arrays and plain objects — never Date
 * objects, DOM nodes, Sets/Maps or functions.
 */
export interface ReportsV2State {
  activeTab: ReportTabKey;
  timeframe: ReportTimeframeKey;
  branch: string;
  role: string;
  user: string;
  customStart: string | null;
  customEnd: string | null;
  /** `"<year>-<month0>"` intake keys; `[]` = all intakes. */
  intakes: string[];
  /**
   * Global custom-field pills (CustomInputs with `isGlobalFilter`):
   * `{ [customInputId]: optionId }`. Serialized to `cf_<id>` params — the
   * same params the Leads page sends; every report repo honors them via the
   * shared lead-where SSOT.
   */
  globalCf: Record<string, string>;
  compare: RequestCompare;
  /** per-card UI state — see `useWidgetState`. */
  widgets: Record<string, unknown>;
}

const COMPARE_OFF: RequestCompare = { type: "", isCompare: false };

/**
 * Reports default to ALL TIME — the whole book at a glance; the user narrows
 * deliberately. Keep in sync with `reset` below and the dirty/active checks in
 * ReportFilterBar.
 */
const initialState: ReportsV2State = {
  activeTab: "lead",
  timeframe: "all",
  branch: "all",
  role: "all",
  user: "all",
  customStart: null,
  customEnd: null,
  intakes: [],
  globalCf: {},
  compare: COMPARE_OFF,
  widgets: {},
};

const reportsV2Slice = createSlice({
  name: "reportsV2",
  initialState,
  reducers: {
    setActiveTab: (s, a: PayloadAction<ReportTabKey>) => {
      s.activeTab = a.payload;
      // The Branch pill lives on Call Analytics only (ReportFilterBar) — it
      // means "calls made by people in this branch" there. Leaving the tab
      // drops it in the same dispatch, so no other report is ever narrowed by
      // a filter it does not show.
      if (a.payload !== "call") s.branch = "all";
    },
    setTimeframe: (s, a: PayloadAction<ReportTimeframeKey>) => {
      s.timeframe = a.payload;
    },
    setBranch: (s, a: PayloadAction<string>) => {
      s.branch = a.payload;
    },
    setRole: (s, a: PayloadAction<string>) => {
      s.role = a.payload;
    },
    /**
     * Dependent rule (unchanged from the context version): picking a specific
     * user pins their role, when the caller knows it. The role id comes from
     * the live filter-options payload — never inferred here.
     */
    setUser: (
      s,
      a: PayloadAction<{ user: string; roleId?: string | null }>,
    ) => {
      s.user = a.payload.user;
      if (a.payload.user !== "all" && a.payload.roleId) {
        s.role = a.payload.roleId;
      }
    },
    setCustomRange: (
      s,
      a: PayloadAction<{ start: string | null; end: string | null }>,
    ) => {
      s.customStart = a.payload.start;
      s.customEnd = a.payload.end;
    },
    setIntakes: (s, a: PayloadAction<string[]>) => {
      s.intakes = a.payload;
    },
    /** Set one global CF pill; null/"all" clears that field's filter. */
    setGlobalCf: (
      s,
      a: PayloadAction<{ fieldId: string; optionId: string | null }>,
    ) => {
      const { fieldId, optionId } = a.payload;
      if (!optionId || optionId === "all") delete s.globalCf[fieldId];
      else s.globalCf[fieldId] = optionId;
    },
    setCompare: (s, a: PayloadAction<RequestCompare>) => {
      s.compare = a.payload;
    },
    toggleCompare: (s) => {
      s.compare = s.compare.isCompare
        ? COMPARE_OFF
        : { type: "previous", isCompare: true };
    },
    /** "Reset all filters" — global filters only; per-card state is left alone
     *  (the bar resets the page scope, not what you'd opened inside a card). */
    reset: (s) => {
      s.timeframe = "all";
      s.branch = "all";
      s.role = "all";
      s.user = "all";
      s.customStart = null;
      s.customEnd = null;
      s.intakes = [];
      s.globalCf = {};
      s.compare = COMPARE_OFF;
    },

    /* ------------------------------------------------------ widget state --- */

    /** Write ONE card's key. Callers go through `useWidgetState`. */
    setWidgetState: (
      s,
      a: PayloadAction<{ key: string; value: unknown }>,
    ) => {
      s.widgets[a.payload.key] = a.payload.value;
    },
    /** Drop a card's keys (prefix match) — "reset this widget". */
    resetWidgetScope: (s, a: PayloadAction<string>) => {
      const prefix = a.payload.endsWith(".") ? a.payload : `${a.payload}.`;
      for (const k of Object.keys(s.widgets)) {
        if (k === a.payload || k.startsWith(prefix)) delete s.widgets[k];
      }
    },
    resetAllWidgets: (s) => {
      s.widgets = {};
    },
  },
});

export const reportsV2Actions = reportsV2Slice.actions;
export default reportsV2Slice.reducer;
